/**
 * Stats section with animated counters
 */

import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { motion } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';
import statsData from "@/data/stats.json";
const stats = statsData.items;

function AnimatedCounter({ value, suffix = '', prefix = '' }: { value: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const { hasIntersected } = useIntersectionObserver({ threshold: 0.5 });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (hasIntersected && !hasAnimated.current) {
      hasAnimated.current = true;
      const duration = 2000;
      const steps = 60;
      const stepValue = value / steps;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        if (currentStep <= steps) {
          setCount(Math.floor(stepValue * currentStep));
        } else {
          setCount(value);
          clearInterval(timer);
        }
      }, stepDuration);

      return () => clearInterval(timer);
    }
  }, [hasIntersected, value]);

  return (
    <span>
      {prefix}
      {count}
      {suffix}
    </span>
  );
}

export function Stats() {
  const { ref, isIntersecting } = useIntersectionObserver({ threshold: 0.2 });

  return (
    <section
      ref={ref}
      className="section"
      style={{
        background: 'linear-gradient(to right, var(--color-lime-500), var(--color-lime-600))',
        color: '#ffffff',
        padding: '2rem 0'
      }}
    >
      <div className="container">
        <div className="columns is-mobile is-multiline is-vcentered">
          {stats.map((stat, index) => (
            <div key={stat.id} className="column is-6-mobile is-3-tablet is-3-desktop" style={{ marginBottom: '1.5rem' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isIntersecting ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="has-text-centered"
              >
                <div className="stat-number" style={{ color: '#ffffff', fontSize: 'clamp(2rem, 5vw, 3rem)' }}>
                  <AnimatedCounter
                    value={typeof stat.value === 'number' ? stat.value : parseInt(stat.value)}
                    suffix={stat.suffix}
                    prefix={stat.prefix}
                  />
                </div>
                <div className="stat-label" style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                  {stat.label}
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
